import { startCase } from 'es-toolkit';
import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Paper,
  Switch,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { Draft, produce } from 'immer';

import { withKey } from '@paragon/client-tools';
import { ComponentSide, EndConditionSettings, isFeaturePatternTruss } from '@paragon/contract-library';
import { editLayoutModelProperties, PropertiesPanel, saveAllProjectChanges } from '@paragon/layout-models';
import { hasEndConditionEditingFeatureFlag } from '@paragon/login-tools';
import { DistanceField, NumericField } from '@paragon/material';
import { assertExhaustiveSwitch, KeyToValueOfType } from '@paragon/utilities';

import Blank from '../../../../../resources/blank.png';
import BottomRibbonLeft from '../../../../../resources/bottomRibbonLeft.png';
import BottomRibbonRight from '../../../../../resources/bottomRibbonRight.png';
import DoubleEndVerticalLeft from '../../../../../resources/doubleEndVerticalLeft.png';
import DoubleEndVerticalRight from '../../../../../resources/doubleEndVerticalRight.png';
import TopAndBottomRibbonLeft from '../../../../../resources/topAndBottomRibbonLeft.png';
import TopAndBottomRibbonRight from '../../../../../resources/topAndBottomRibbonRight.png';
import TopChordBearingLeft from '../../../../../resources/topChordBearingLeft.png';
import TopChordBearingRight from '../../../../../resources/topChordBearingRight.png';
import TopChordBearingWithBlockLeft from '../../../../../resources/topChordBearingWithBlockLeft.png';
import TopChordBearingWithBlockRight from '../../../../../resources/topChordBearingWithBlockRight.png';
import TopRibbonLeft from '../../../../../resources/topRibbonLeft.png';
import TopRibbonRight from '../../../../../resources/topRibbonRight.png';
import {
  DEFAULT_END_CONDITION_PRESETS,
  getSourcePreset,
  GlobalEndConditionPattern,
} from '../../../../library/defaultEndConditionPresets';
import { editEndCondition, setInsertionEndConditionSettings } from '../../../../store/actions/editorActions';
import { getActiveFeaturePatternTrussSettings } from '../../../../store/selectors/activeComponentFeaturePatternSettingsSelectors';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { getProjectLayoutModel } from '../../../../store/selectors/getProjectLayoutModel';
import { settingsShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { useConvertToSettingsLauncher } from '../../../misc/useConvertToSettingsLauncher';
import EndConditionPatternButton from './EndConditionPatternButton';
import { usePropertiesPanelSaveButtons } from './usePropertiesPanelSaveButtons';

const getDefaultTopChordCutBack = (settings: EndConditionSettings) =>
  !settings.isTopChordBearing && settings.topRibbonHeight > 0 ? settings.topRibbonWidth : 0;

const getDefaultBottomChordCutBack = (settings: EndConditionSettings) => {
  const topChordBearingAdjustment = settings.isTopChordBearing
    ? settings.topChordCutBackDistance + settings.bearingWidth + settings.bearingGap
    : 0;
  const ribbonAdjustment = settings.bottomRibbonHeight > 0 ? settings.bottomRibbonWidth : 0;
  return topChordBearingAdjustment + ribbonAdjustment;
};

function adjustCutBackDistances(previousSettings: EndConditionSettings, newSettings: Draft<EndConditionSettings>) {
  newSettings.topChordCutBackDistance +=
    getDefaultTopChordCutBack(newSettings) - getDefaultTopChordCutBack(previousSettings);
  newSettings.bottomChordCutBackDistance +=
    getDefaultBottomChordCutBack(newSettings) - getDefaultBottomChordCutBack(previousSettings);
}

interface Props {
  readonly disabled?: boolean;
  readonly side: ComponentSide;
  readonly defaultPattern: GlobalEndConditionPattern | 'advancedSettings';

  readonly value: EndConditionSettings;
  readonly onChange: (newValue: EndConditionSettings) => void;
}

type EndConditionPattern = GlobalEndConditionPattern | number;

export function EndConditionSettingsPanel(props: Props) {
  const dispatch = useAppDispatch();
  const project = useAppSelector(getProjectLayoutModel);

  const [selectedPattern, setSelectedPattern] = useState<EndConditionPattern>(
    props.defaultPattern === 'advancedSettings' ? 'blank' : props.defaultPattern,
  );
  const [advancedSettings, setAdvancedSettings] = useState<boolean>(props.defaultPattern === 'advancedSettings');

  const isLeftSide = props.side === 'Left';

  const customConditions = () => {
    setAdvancedSettings(true);
    props.onChange(
      produce(props.value, (draft) => {
        adjustCutBackDistances(props.value, draft);
      }),
    );
  };

  const defaultConditions = () => {
    setSelectedPattern('blank');
    setAdvancedSettings(false);
    props.onChange({ ...props.value, ...DEFAULT_END_CONDITION_PRESETS.blank });
  };

  const updateSelectedPreset = () => {
    if (!project || typeof selectedPattern !== 'number') return;

    const newPresets = [...(isLeftSide ? project.endConditionPresetsLeft : project.endConditionPresetsRight)];
    newPresets[selectedPattern] = props.value;

    dispatch(
      editLayoutModelProperties(
        project.guid,
        isLeftSide ? { endConditionPresetsLeft: newPresets } : { endConditionPresetsRight: newPresets },
      ),
    );
    dispatch(saveAllProjectChanges());

    setAdvancedSettings(getSourcePreset(props.value) === 'blank');
  };

  const saveNewPreset = () => {
    if (!project) return;

    const newPresets = [...(isLeftSide ? project.endConditionPresetsLeft : project.endConditionPresetsRight)];
    newPresets.push(props.value);

    dispatch(
      editLayoutModelProperties(
        project.guid,
        isLeftSide ? { endConditionPresetsLeft: newPresets } : { endConditionPresetsRight: newPresets },
      ),
    );
    dispatch(saveAllProjectChanges());

    setSelectedPattern(newPresets.length - 1);
    setAdvancedSettings(getSourcePreset(props.value) === 'blank');
  };

  const deletePreset = () => {
    if (!project || typeof selectedPattern !== 'number') return;

    const newPresets = [...(isLeftSide ? project.endConditionPresetsLeft : project.endConditionPresetsRight)];
    newPresets.splice(selectedPattern, 1);

    dispatch(
      editLayoutModelProperties(
        project.guid,
        isLeftSide ? { endConditionPresetsLeft: newPresets } : { endConditionPresetsRight: newPresets },
      ),
    );
    dispatch(saveAllProjectChanges());

    defaultConditions();
  };

  const onChangeDistance = (key: KeyToValueOfType<EndConditionSettings, number>) => (value: number) => {
    props.onChange(
      produce(props.value, (draft) => {
        draft[key] = value;
        adjustCutBackDistances(props.value, draft);
      }),
    );
  };

  const checkedChange =
    (name: KeyToValueOfType<EndConditionSettings, boolean>) => (event: React.ChangeEvent<HTMLInputElement>) => {
      props.onChange(
        produce(props.value, (draft) => {
          const isStackTopChord = props.value.topChordSlider;
          if (name === 'isTopChordBearing' && isStackTopChord) {
            draft.topChordSlider = event.target.checked;
          }

          draft[name] = event.target.checked;

          adjustCutBackDistances(props.value, draft);
        }),
      );
    };

  const globalPresetConditions = (pattern: GlobalEndConditionPattern) => () => {
    setSelectedPattern(pattern);
    setAdvancedSettings(false);
    props.onChange({ ...props.value, ...DEFAULT_END_CONDITION_PRESETS[pattern] });
  };

  const removeRibbons = () => {
    props.onChange(
      produce(props.value, (draft) => {
        draft.topRibbonHeight = 0;
        draft.topRibbonWidth = 0;
        draft.bottomRibbonHeight = 0;
        draft.bottomRibbonWidth = 0;

        adjustCutBackDistances(props.value, draft);
      }),
    );
  };

  const removeBottomRibbon = () => {
    props.onChange(
      produce(props.value, (draft) => {
        draft.bottomRibbonHeight = 0;
        draft.bottomRibbonWidth = 0;
        adjustCutBackDistances(props.value, draft);
      }),
    );
  };

  const removeTopRibbon = () => {
    props.onChange(
      produce(props.value, (draft) => {
        draft.topRibbonHeight = 0;
        draft.topRibbonWidth = 0;
        adjustCutBackDistances(props.value, draft);
      }),
    );
  };

  const makeDistanceFieldFor = (
    key: KeyToValueOfType<EndConditionSettings, number>,
    sx?: SxProps<Theme>,
    propertyOverride?: string,
    disabled?: boolean,
  ) =>
    props.value && (
      <DistanceField
        label={propertyOverride ?? startCase(key)}
        value={props.value[key]}
        onSubmit={onChangeDistance(key)}
        fullWidth={false}
        sx={{ m: 1, ...sx }}
        disabled={disabled || props.disabled}
      />
    );

  if (!props.side || !props.value) {
    return null;
  }

  const stackTopChord = props.value.isTopChordBearing ? (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={props.value.topChordSlider}
          onChange={checkedChange('topChordSlider')}
          disabled={props.disabled}
        />
      }
      label="Stack Top Chord"
    />
  ) : null;
  const chordStackLength = props.value.isTopChordBearing
    ? makeDistanceFieldFor('topChordSliderLength', { flex: 1 }, 'Top Chord Slider Length', !props.value.topChordSlider)
    : null;

  const bearingGap = props.value.isTopChordBearing ? makeDistanceFieldFor('bearingGap', { flex: 1 }) : null;
  const bearingBlockWidth = props.value.isTopChordBearing
    ? makeDistanceFieldFor('bearingBlockWidth', { flex: 1 })
    : null;
  const blockElevation = props.value.isTopChordBearing
    ? makeDistanceFieldFor('bearingBlockElevation', { flex: 1 })
    : null;
  const blockMeasureLocation = props.value.isTopChordBearing ? (
    <FormControlLabel
      sx={{ m: 1, flex: 1, justifyContent: 'center' }}
      control={
        <Switch
          sx={{ rotate: '-90deg' }}
          size="small"
          color="default"
          checked={props.value.measureBearingBlockFromTop}
          onChange={checkedChange('measureBearingBlockFromTop')}
          disabled={props.disabled}
        />
      }
      label={props.value.measureBearingBlockFromTop ? 'From Top' : 'From Bottom'}
    />
  ) : null;

  const hasTopRibbon = props.value.topRibbonWidth && props.value.topRibbonHeight !== 0;
  const hasBottomRibbon = props.value.bottomRibbonWidth && props.value.bottomRibbonHeight !== 0;

  const removeTopRibbonButton =
    hasTopRibbon && !hasBottomRibbon ? (
      <Button sx={{ m: 1 }} variant="outlined" onClick={removeTopRibbon} disabled={props.disabled}>
        Remove Top Ribbon
      </Button>
    ) : null;

  const removeBottomRibbonButton =
    !hasTopRibbon && hasBottomRibbon ? (
      <Button sx={{ m: 1 }} variant="outlined" onClick={removeBottomRibbon} disabled={props.disabled}>
        Remove Bottom Ribbon
      </Button>
    ) : null;

  const removeRibbonsButton =
    hasTopRibbon && hasBottomRibbon ? (
      <Button sx={{ m: 1 }} variant="outlined" onClick={removeRibbons} disabled={props.disabled}>
        Remove Ribbons
      </Button>
    ) : null;

  const ribbons = !props.value.isTopChordBearing ? (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {makeDistanceFieldFor('topRibbonHeight', { flex: 1 })}
        {makeDistanceFieldFor('topRibbonWidth', { flex: 1 })}
      </Box>
      {removeTopRibbonButton}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {makeDistanceFieldFor('bottomRibbonHeight', { flex: 1 })}
        {makeDistanceFieldFor('bottomRibbonWidth', { flex: 1 })}
      </Box>
      {removeBottomRibbonButton}
      {removeRibbonsButton}
      {makeDistanceFieldFor('parapetHeight')}
    </>
  ) : null;

  const endVerticalInput = (
    <NumericField
      sx={!props.value.isTopChordBearing ? { m: 1 } : { m: 1, flex: 1 }}
      label="End Vertical Count"
      value={props.value.endVerticalCount}
      onSubmit={(endVerticalCount) => props.onChange({ ...props.value, endVerticalCount })}
      disabled={props.disabled}
    />
  );

  let settings: React.JSX.Element | null;
  const settingsPattern = typeof selectedPattern === 'number' ? getSourcePreset(props.value) : selectedPattern;
  if (advancedSettings) {
    settings = (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <FormControlLabel
            sx={{ fontSize: '.95rem' }}
            control={
              <Checkbox
                size="small"
                checked={props.value.isTopChordBearing}
                onChange={checkedChange('isTopChordBearing')}
                disabled={props.disabled}
              />
            }
            label="Top Chord Bearing"
          />
          {stackTopChord}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {endVerticalInput}
          {chordStackLength}
        </Box>
        <Box sx={{ display: 'flex' }}>
          {makeDistanceFieldFor('topChordCutBackDistance', { flex: 1 }, 'Top Chord Cut Back')}
          {makeDistanceFieldFor('bottomChordCutBackDistance', { flex: 1 }, 'Bottom Chord Cut Back')}
        </Box>
        {ribbons}
        <Box sx={{ display: 'flex' }}>
          {blockElevation}
          {blockMeasureLocation}
        </Box>
        <Box sx={{ display: 'flex' }}>
          {bearingGap}
          {bearingBlockWidth}
        </Box>
        {makeDistanceFieldFor('bearingWidth')}
      </>
    );
  } else
    switch (settingsPattern) {
      case 'topRibbon':
        settings = (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>{endVerticalInput}</Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {makeDistanceFieldFor('topRibbonHeight', { flex: 1 })}
              {makeDistanceFieldFor('topRibbonWidth', { flex: 1 })}
            </Box>
            {makeDistanceFieldFor('bearingWidth')}
          </Box>
        );
        break;
      case 'bottomRibbon':
        settings = (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>{endVerticalInput}</Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {makeDistanceFieldFor('bottomRibbonHeight', { flex: 1 })}
              {makeDistanceFieldFor('bottomRibbonWidth', { flex: 1 })}
            </Box>
            {makeDistanceFieldFor('bearingWidth')}
          </Box>
        );
        break;
      case 'topAndBottomRibbon':
        settings = (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>{endVerticalInput}</Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {makeDistanceFieldFor('topRibbonHeight', { flex: 1 })}
              {makeDistanceFieldFor('topRibbonWidth', { flex: 1 })}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {makeDistanceFieldFor('bottomRibbonHeight', { flex: 1 })}
              {makeDistanceFieldFor('bottomRibbonWidth', { flex: 1 })}
            </Box>
            {makeDistanceFieldFor('bearingWidth')}
          </Box>
        );
        break;
      case 'doubleEndVertical':
        settings = (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>{endVerticalInput}</Box>
            {makeDistanceFieldFor('bearingWidth')}
          </Box>
        );
        break;
      case 'topChordBearing':
        settings = (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={props.value.isTopChordBearing}
                    onChange={checkedChange('isTopChordBearing')}
                    disabled={props.disabled}
                  />
                }
                label="Top Chord Bearing"
              />
              {stackTopChord}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
              {endVerticalInput}
              {chordStackLength}
            </Box>
            {makeDistanceFieldFor('bearingWidth')}
          </Box>
        );
        break;
      case 'topChordBearingWithBlock':
        settings = (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={props.value.isTopChordBearing}
                    onChange={checkedChange('isTopChordBearing')}
                    disabled={props.disabled}
                  />
                }
                label="Top Chord Bearing"
              />
              {stackTopChord}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
              {endVerticalInput}
              {chordStackLength}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {bearingGap}
              {bearingBlockWidth}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {blockElevation}
              {blockMeasureLocation}
            </Box>
            {makeDistanceFieldFor('bearingWidth')}
          </Box>
        );
        break;
      case 'blank':
        settings = null;
        break;
      default:
        assertExhaustiveSwitch(settingsPattern);
    }

  const customPresetButtons: React.JSX.Element[] = [];
  const customPresetModifierButtons: React.JSX.Element[] = [];
  if (project) {
    const customPresets = isLeftSide ? project.endConditionPresetsLeft : project.endConditionPresetsRight;
    const buttonsList: React.JSX.Element[] = [];
    const counts: Record<GlobalEndConditionPattern, number> = {
      blank: 0,
      topRibbon: 0,
      bottomRibbon: 0,
      topAndBottomRibbon: 0,
      doubleEndVertical: 0,
      topChordBearing: 0,
      topChordBearingWithBlock: 0,
    };
    for (let i = 0; i < customPresets.length; i++) {
      const pattern = getSourcePreset(customPresets[i]);
      counts[pattern] += 1;
      const index = counts[pattern];
      let src: string;
      let alt: string;
      switch (pattern) {
        case 'blank':
          src = Blank;
          alt = 'Advanced';
          break;
        case 'topRibbon':
          src = isLeftSide ? TopRibbonLeft : TopRibbonRight;
          alt = 'Top Ribbon';
          break;
        case 'bottomRibbon':
          src = isLeftSide ? BottomRibbonLeft : BottomRibbonRight;
          alt = 'Bottom Ribbon';
          break;
        case 'topAndBottomRibbon':
          src = isLeftSide ? TopAndBottomRibbonLeft : TopAndBottomRibbonRight;
          alt = 'Top and Bottom Ribbon';
          break;
        case 'doubleEndVertical':
          src = isLeftSide ? DoubleEndVerticalLeft : DoubleEndVerticalRight;
          alt = 'Double End Vertical';
          break;
        case 'topChordBearing':
          src = isLeftSide ? TopChordBearingLeft : TopChordBearingRight;
          alt = 'Top Chord Bearing';
          break;
        case 'topChordBearingWithBlock':
          src = isLeftSide ? TopChordBearingWithBlockLeft : TopChordBearingWithBlockRight;
          alt = 'Top Chord Bearing with Block';
          break;
        default:
          assertExhaustiveSwitch(pattern);
      }

      buttonsList.push(
        <EndConditionPatternButton
          key={i}
          src={src}
          alt={alt}
          onClick={() => {
            setSelectedPattern(i);
            setAdvancedSettings(pattern === 'blank');
            props.onChange(customPresets[i]);
          }}
          isSelected={i === selectedPattern}
          index={index}
        />,
      );
    }

    for (let i = 0; i < buttonsList.length; i += 3) {
      customPresetButtons.push(
        <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }} key={i}>
          {buttonsList[i]}
          {buttonsList[i + 1]}
          {buttonsList[i + 2]}
        </Box>,
      );
    }

    if (typeof selectedPattern === 'number')
      customPresetModifierButtons.push(
        <Button sx={{ m: 1, flex: 1 }} variant="outlined" onClick={updateSelectedPreset} key={1}>
          Update
        </Button>,
      );
    if (settings)
      customPresetModifierButtons.push(
        <Button
          sx={{ m: 1, flex: 1 }}
          variant="outlined"
          onClick={saveNewPreset}
          key={2}
          disabled={customPresets.length >= 9}
        >
          {customPresets.length >= 9 ? 'Limit 9' : 'New'}
        </Button>,
      );
    if (typeof selectedPattern === 'number')
      customPresetModifierButtons.push(
        <Button sx={{ m: 1, flex: 1 }} variant="outlined" onClick={deletePreset} key={3}>
          Delete
        </Button>,
      );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: 1, justifyContent: 'space-evenly' }}>
      {!props.disabled && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
            <EndConditionPatternButton
              src={isLeftSide ? TopRibbonLeft : TopRibbonRight}
              alt="Top Ribbon"
              onClick={globalPresetConditions('topRibbon')}
              isSelected={selectedPattern === 'topRibbon'}
            />
            <EndConditionPatternButton
              src={isLeftSide ? BottomRibbonLeft : BottomRibbonRight}
              alt="Bottom Ribbon"
              onClick={globalPresetConditions('bottomRibbon')}
              isSelected={selectedPattern === 'bottomRibbon'}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
            <EndConditionPatternButton
              src={isLeftSide ? TopAndBottomRibbonLeft : TopAndBottomRibbonRight}
              alt="Top and Bottom Ribbon"
              onClick={globalPresetConditions('topAndBottomRibbon')}
              isSelected={selectedPattern === 'topAndBottomRibbon'}
            />
            <EndConditionPatternButton
              src={isLeftSide ? DoubleEndVerticalLeft : DoubleEndVerticalRight}
              alt="Double End Vertical"
              onClick={globalPresetConditions('doubleEndVertical')}
              isSelected={selectedPattern === 'doubleEndVertical'}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
            <EndConditionPatternButton
              src={isLeftSide ? TopChordBearingLeft : TopChordBearingRight}
              alt="Top Chord Bearing"
              onClick={globalPresetConditions('topChordBearing')}
              isSelected={selectedPattern === 'topChordBearing'}
            />
            <EndConditionPatternButton
              src={isLeftSide ? TopChordBearingWithBlockLeft : TopChordBearingWithBlockRight}
              alt="Top Chord Bearing with Block"
              onClick={globalPresetConditions('topChordBearingWithBlock')}
              isSelected={selectedPattern === 'topChordBearingWithBlock'}
            />
          </Box>
          {customPresetButtons}
          <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
            <Button sx={{ m: 1, flex: 1 }} variant="outlined" onClick={customConditions}>
              Customize
            </Button>
            <Button sx={{ m: 1, flex: 1 }} variant="outlined" onClick={defaultConditions}>
              Reset
            </Button>
          </Box>
        </>
      )}

      {settings}
      <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>{customPresetModifierButtons}</Box>
    </Box>
  );
}

export function InsertionComponentEndConditionSettingsPanel(props: { readonly side: ComponentSide }) {
  const dispatch = useAppDispatch();
  const isInViewOnlyMode = useAppSelector(settingsShouldBeViewOnly);
  const featurePatternSettings = useAppSelector(getActiveFeaturePatternTrussSettings);

  let endConditionSettings: EndConditionSettings | undefined;
  switch (props.side) {
    case 'Left':
      endConditionSettings = featurePatternSettings?.leftEndSettings;
      break;

    case 'Right':
      endConditionSettings = featurePatternSettings?.rightEndSettings;
      break;
  }

  if (!endConditionSettings) {
    return null;
  }

  return (
    <EndConditionSettingsPanel
      defaultPattern="blank"
      side={props.side}
      value={endConditionSettings}
      disabled={isInViewOnlyMode}
      onChange={(value) => dispatch(setInsertionEndConditionSettings(props.side, value))}
    />
  );
}

export const ActiveComponentEndConditionPropertiesPanel = withKey('guid', (props: { guid: string }) => {
  const dispatch = useAppDispatch();
  const isInViewOnlyMode = useAppSelector(settingsShouldBeViewOnly);
  const activeComponentDesign = useAppSelector(getActiveComponentDesign);
  const saveButtons = usePropertiesPanelSaveButtons((newValue: EndConditionSettings) =>
    dispatch(editEndCondition(activeComponentDesign!, newValue)),
  );

  const hasFeatureFlag = useAppSelector(hasEndConditionEditingFeatureFlag);

  const [renderConvertToSettingsDialog, launchConvertToSettings] = useConvertToSettingsLauncher();

  if (!activeComponentDesign || !isFeaturePatternTruss(activeComponentDesign.component)) {
    console.error('Attempted to render EndConditionSettings for non-feature-pattern truss.');
    return null;
  }

  if (!hasFeatureFlag) {
    return (
      <>
        {renderConvertToSettingsDialog()}

        <Paper elevation={0} variant="outlined" sx={{ marginTop: 1 }}>
          <Typography variant="body1" color="textSecondary" sx={{ margin: 2 }}>
            Editing end conditions is not yet supported. You can manually edit members, or regenerate this component
            using{' '}
            <Link sx={{ cursor: 'pointer' }} onClick={() => void launchConvertToSettings()}>
              Convert to Settings.
            </Link>
          </Typography>
        </Paper>
      </>
    );
  }

  const featurePatternSettings = activeComponentDesign.component.inferredSettings;
  if (!featurePatternSettings) {
    return <Typography>Unable to edit end condition due to an issue inferring the truss parameters.</Typography>;
  }

  let side: ComponentSide;
  let endConditionSettings: EndConditionSettings;

  if (featurePatternSettings.leftEndSettings.guid === props.guid) {
    side = 'Left';
    endConditionSettings = featurePatternSettings.leftEndSettings;
  } else if (featurePatternSettings.rightEndSettings.guid === props.guid) {
    side = 'Right';
    endConditionSettings = featurePatternSettings.rightEndSettings;
  } else {
    throw new Error('Attempted to render EndConditionSettings with invalid guid.');
  }

  return (
    <PropertiesPanel title={`End Condition ${side}`}>
      <EndConditionSettingsPanel
        defaultPattern="advancedSettings"
        side={side}
        value={saveButtons.value ?? endConditionSettings}
        disabled={isInViewOnlyMode}
        onChange={saveButtons.setValue}
      />
      <Box sx={{ display: 'flex', m: 1 }}>{!isInViewOnlyMode && saveButtons.render()}</Box>
    </PropertiesPanel>
  );
});
